<script setup lang="ts">
import { computed, toRef } from "vue";
import { ArrowDown } from "lucide-vue-next";
import { useAnalysisQuery } from "@/composables/useAnalysisQuery";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { removeHtmlMarkdownAndLineBreak } from "@utils/toolBox";
import { Tooltip } from "@/components/ui/tooltip";
import type { AnalysisCount, FunnelData, FunnelStage } from "@/types";
import { cardCodes, isLightColor, shadeRamp, useAnalysisContext, useCardPopulation } from "../analysisData";
import AnalysisCardShell from "./AnalysisCardShell.vue";
import PopulationSwitch from "./PopulationSwitch.vue";

const props = defineProps({
  card: { type: Object, required: true },
  readonly: { type: Boolean, default: false },
});

const card = toRef(props, "card");
const { weighted, availableCards, displayLocale } = useAnalysisContext();
const { choices, selected, filters, color: populationColor } = useCardPopulation(card);

const stages = computed<FunnelStage[]>(() => props.card.content?.stages || []);
const validStages = computed(() => stages.value.filter((stage) => stage.question_id && stage.codes?.length));

const body = computed(() => {
  if (validStages.value.length < 2) return null;
  return {
    stages: validStages.value.map((stage) => ({ question_id: stage.question_id, codes: stage.codes })),
    filters: filters.value,
  };
});

const { result, loading, state, requested } = useAnalysisQuery<FunnelData>("funnel", body);

// The stages the current result was computed for. `content.stages` moves on as soon as the
// user edits the panel, up to 300 ms plus a round trip before the next result lands.
const resultStages = computed<FunnelStage[]>(() => {
  const sent = (requested.value?.stages as { question_id: number; codes: string[] }[] | undefined) || [];
  return sent.map((stage) => {
    const current = validStages.value.find(
      (s) => s.question_id === stage.question_id && s.codes.join(".") === stage.codes.join("."),
    );
    return { ...stage, label: current?.label || "" };
  });
});

const count = (stage: AnalysisCount) => (weighted.value ? stage.weighted_nb : stage.nb);

const respondents = computed(() => {
  if (!result.value) return null;
  return weighted.value ? result.value.weighted_respondents : result.value.respondents;
});

const resolveTitle = (title: unknown) => removeHtmlMarkdownAndLineBreak(resolveLocalizedTitle(title, displayLocale.value));

const cardOf = (stage: FunnelStage) =>
  availableCards.value.find((c) => (c.questions || []).some((q: { id: number }) => q.id === stage.question_id));

function stageLabel(stage: FunnelStage, idx: number) {
  if (stage.label) return stage.label;
  const meta = cardOf(stage);
  if (!meta) return `Stage ${idx + 1}`;
  const question = (meta.questions || []).find((q: { id: number }) => q.id === stage.question_id);
  const item = resolveTitle(question?.item_title);
  return item ? `${meta.name} · ${item}` : meta.name || `Stage ${idx + 1}`;
}

function stageAnswers(stage: FunnelStage) {
  const codes = cardCodes(cardOf(stage), resolveTitle);
  return (stage.codes || []).map((code) => codes.find((c) => c.code === code)?.label || code);
}

const accent = computed(() => props.card.options?.color || populationColor.value);

const rows = computed(() => {
  const data = result.value;
  if (!data?.stages?.length || data.stages.length !== resultStages.value.length) return [];
  const base = Math.max(respondents.value || 0, 1);
  const first = Math.max(count(data.stages[0]), 1);
  const colors = shadeRamp(accent.value, data.stages.length);
  return data.stages.map((stage, idx) => {
    const nb = count(stage);
    const previous = idx === 0 ? null : count(data.stages[idx - 1]);
    return {
      label: stageLabel(resultStages.value[idx], idx),
      answers: stageAnswers(resultStages.value[idx]),
      nb: Math.round(nb),
      share: (nb / base) * 100,
      // Bar widths are relative to the funnel's own first stage.
      width: (nb / first) * 100,
      conversion: previous == null ? null : previous > 0 ? (nb / previous) * 100 : 0,
      color: colors[idx],
      light: isLightColor(colors[idx]),
    };
  });
});

const cardState = computed(() => state(rows.value.length > 0 && (respondents.value || 0) > 0));

const formatPercent = (value: number) => `${Math.round(value)}%`;
</script>

<template>
  <AnalysisCardShell
    :card="card"
    :readonly="readonly"
    :respondents="respondents"
    :weighted="weighted"
    respondents-hint="Respondents of the selected population, the base of every share."
    description="Share of respondents reaching each stage. A stage only counts respondents who also passed every stage above it, so each conversion is a real one."
    :state="cardState"
    state-message="Define at least two stages, a question and its answers each, to build the funnel.">
    <template #toolbar>
      <PopulationSwitch v-if="choices.length > 1" :card="card" :populations="choices" :selected="selected" />
    </template>

    <div class="flex flex-col items-center gap-1 py-2" :class="{ 'opacity-50': loading }">
      <template v-for="(row, idx) in rows" :key="idx">
        <div v-if="row.conversion != null" class="flex items-center gap-1 text-xs font-medium text-muted-foreground">
          <ArrowDown class="h-3 w-3" />
          {{ formatPercent(row.conversion) }}
        </div>
        <Tooltip :delay-duration="150" side="right" content-class="max-w-xs">
          <div
            class="flex h-11 min-w-[9rem] flex-col items-center justify-center rounded-md transition-all duration-300"
            :class="row.light ? 'text-foreground' : 'text-white drop-shadow-sm'"
            :style="{ width: `${Math.max(row.width, 14)}%`, backgroundColor: row.color }">
            <span class="max-w-full truncate px-2 text-xs font-medium">{{ row.label }}</span>
            <span class="text-2xs tabular-nums">{{ formatPercent(row.share) }} · {{ row.nb }}</span>
          </div>
          <template #content>
            <div class="font-medium">{{ row.label }}</div>
            <div class="tabular-nums">{{ formatPercent(row.share) }} of the base · {{ row.nb }} respondents</div>
            <div v-if="row.conversion != null" class="tabular-nums">{{ formatPercent(row.conversion) }} of the previous stage</div>
            <ul v-if="row.answers.length" class="mt-1 space-y-0.5 border-t border-white/20 pt-1">
              <li v-for="(answer, answerIdx) in row.answers" :key="answerIdx" class="truncate">· {{ answer }}</li>
            </ul>
          </template>
        </Tooltip>
      </template>
    </div>
  </AnalysisCardShell>
</template>
